import { NgClass, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ActivityLocationFacade } from '@curbnturf/activity/src/lib/+state/activity.facade';
import { LoginFirstModalComponent } from '@curbnturf/auth-modal/src/lib/login-first-modal/login-first-modal.component';
import { BoondockFacade } from '@curbnturf/boondock-listing';
import { DonationModalPage } from '@curbnturf/credit-card';
import { MiscSvgComponent, SiteSvgsComponent } from '@curbnturf/custom-svgs';
import {
  ActivityLocation,
  IBoondockPoint,
  IListingPoint,
  IRoutePoint,
  LatLon,
  Listing,
  RoutePoint,
  User,
} from '@curbnturf/entities';
import { ListingFacade } from '@curbnturf/listing';
import { NavigateToHelper } from '@curbnturf/map/src/lib/geocoding/navigate-to-helper.service';
import { PlatformService } from '@curbnturf/network/src/lib/platform/platform.service';
import { RatingBoondockModalControllerComponent, RatingPoiPageComponent } from '@curbnturf/rating-components';
import { TripPlannerFacade } from '@curbnturf/trip-planner/src/lib/+state/trip-planner.facade';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule, ModalController } from '@ionic/angular';
import { Subscription } from 'rxjs';

@Component({
  standalone: true,
  selector: 'curbnturf-save-favorite',
  templateUrl: './save-favorite.component.html',
  styleUrls: ['./save-favorite.component.scss'],
  imports: [
    IonicModule,
    MiscSvgComponent,
    NgClass,
    NgIf,
    SiteSvgsComponent,
  ],
})
export class SaveFavoriteComponent implements OnDestroy, OnInit {
  @Input() point?: IListingPoint;
  @Input() boondockPoint?: IBoondockPoint;
  @Input() poi?: ActivityLocation;
  @Input() icon: boolean = false;
  @Input() size: string = 'medium';
  @Input() right: boolean = false;
  @Input() updateRoute: boolean = false;

  favorited = false;

  user: User;

  get addedToTrip(): boolean {
    if (this.trip.length > 0) {
      if (this.point?.id) {
        const listingFound = this.trip.find((el) => {
          if (el.isListing && this.point?.id && el.listing?.id) {
            return el.listing?.id === this.point.id;
          }
          return undefined;
        });
        if (listingFound) {
          return true;
        }
      } else if (this.boondockPoint?.id) {
        const boondockFound = this.trip.find((el) => {
          if (el.isBoondock && this.boondockPoint?.id && el.boondock?.id) {
            return el.boondock?.id === this.boondockPoint.id;
          }
          return undefined;
        });

        if (boondockFound) {
          return true;
        }
      } else if (this.poi?.id) {
        const poiFound = this.trip.find((el) => {
          if (el.isPoi && this.poi?.id && el.poi?.id) {
            return el.poi?.id === this.poi.id;
          }
          return undefined;
        });

        if (poiFound) {
          return true;
        }
      }
    }

    return false;
  }

  private trip: IRoutePoint[] = [];
  private subscriptions: Subscription[] = [];

  constructor(
    private poiFacade: ActivityLocationFacade,
    private route: ActivatedRoute,
    private boondockFacade: BoondockFacade,
    private cd: ChangeDetectorRef,
    private modalController: ModalController,
    private navigateToHelper: NavigateToHelper,
    private platformService: PlatformService,
    private listingFacade: ListingFacade,
    private tripPlannerFacade: TripPlannerFacade,
    private userFacade: UserFacade,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.userFacade.currentUser$.subscribe((user) => {
        this.user = user;
        if (this.point) {
          this.favorited = !!this.user?.listingFavorites?.find((favorite) => favorite.listingId === this.point?.id);
        } else if (this.boondockPoint) {
          this.favorited = !!this.user?.boondockFavorites?.find((favorite) => favorite.boondockId === this.boondockPoint?.id);
        } else if (this.poi) {
          this.favorited = !!this.user?.poiFavorites?.find((favorite) => favorite.poiId === this.poi?.id);
        }
        this.cd.detectChanges();
      }),
      this.tripPlannerFacade.trip$.subscribe((trip) => {
        if (trip && trip.length > 0) {
          this.trip = trip;
        } else {
          this.trip = [];
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }

  async openModalSave() {
    await this.openLoginModal('To save you must be logged in.');
  }

  async openModalRate() {
    await this.openLoginModal('To rate you must be logged in.');
  }

  async openModalDonate() {
    await this.openLoginModal('To donate you must be logged in.');
  }

  favorite() {
    if (this.point?.id) {
      this.listingFacade.favorite(this.point);
    } else if (this.boondockPoint?.id) {
      this.boondockFacade.favorite(this.boondockPoint);
    } else if (this.poi?.id) {
      this.poiFacade.favorite(this.poi);
    }
    this.favorited = true;
    this.cd.detectChanges();
  }

  unfavorite() {
    if (this.point?.id) {
      this.listingFacade.unfavorite(this.point);
    } else if (this.boondockPoint?.id) {
      this.boondockFacade.unfavorite(this.boondockPoint);
    } else if (this.poi?.id) {
      this.poiFacade.unfavorite(this.poi);
    }
    this.favorited = false;
    this.cd.detectChanges();
  }

  navigate() {
    if (this.boondockPoint) {
      window.open(this.navigateToHelper.makeNavigationLinkFromBoondockPoint(this.boondockPoint));
    } else if (this.poi) {
      window.open(this.navigateToHelper.makeLinkFromActivity(this.poi));
    }
  }

  async rate() {
    if (this.boondockPoint) {
      this.boondockFacade.select(this.boondockPoint.id);
      const modal = await this.modalController.create({
        component: RatingBoondockModalControllerComponent,
        componentProps: {
          boondockId: this.boondockPoint.id,
        },
      });
      await modal.present();
    } else if (this.poi?.id) {
      this.poiFacade.load(this.poi.id);
      const modal = await this.modalController.create({
        component: RatingPoiPageComponent,
        componentProps: {
          poi: this.poi,
        },
      });
      await modal.present();
    }
  }

  async donate() {
    if (this.boondockPoint) {
      const modal = await this.modalController.create({
        component: DonationModalPage,
        componentProps: {
          site: this.boondockPoint,
        },
      });
      await modal.present();
    } else if (this.poi?.id) {
      const modal = await this.modalController.create({
        component: DonationModalPage,
        componentProps: {
          poi: this.poi,
        },
      });
      await modal.present();
    }
  }

  @HostListener('click', ['$event'])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public onClick(event: any): void {
    event.stopPropagation();
  }

  addToTrip() {
    let updateRoute = false;
    if (
      (this.route.snapshot.url.length === 0 || this.route.snapshot.url[0]?.toString() === 'search') &&
      !this.platformService.isAppContext()
    ) {
      updateRoute = true;
    }
    let point = new RoutePoint({ isActive: true, coordinates: { lat: 0, lon: 0 } });

    if (this.poi) {
      point.poi = this.poi;
      point.isPoi = true;
      point.coordinates = new LatLon({
        lat: this.poi.coordinates.lat,
        lon: this.poi.coordinates.lon,
      });
    } else if (this.boondockPoint) {
      point = new RoutePoint({
        isBoondock: true,
        boondock: {
          id: this.boondockPoint.id,
          coordinates: {
            ...this.boondockPoint.coordinates,
          },
          name: this.boondockPoint.name,
          address: this.boondockPoint.address,
          photos: this.boondockPoint.photos,
          rating: this.boondockPoint.rating,
          ratingCount: this.boondockPoint.ratingCount,
        },
        name: this.boondockPoint.name,
        coordinates: { ...this.boondockPoint.coordinates },
        isActive: true,
      });
    } else if (this.point) {
      point.isListing = true;
      point.coordinates = new LatLon({
        lat: this.point.coordinates.lat,
        lon: this.point.coordinates.lon,
      });
      point.listing = new Listing({
        id: this.point.id || 0,
        name: this.point.name || '',
        address: this.point.address,
        photos: this.point.photos || [],
        coordinates: this.point.coordinates || new LatLon({ lat: 0, lon: 0 }),
        rating: this.point.rating || 1,
        ratingCount: this.point.ratingCount || 0,
      });
    }
    this.tripPlannerFacade.addPoint(point, updateRoute);
  }

  removeFromTrip() {
    const foundPoint = this.trip.find(
      (el) =>
        el.listing?.id === this.point?.id || el.poi?.id === this.poi?.id || el.boondock?.id === this.boondockPoint?.id,
    );
    if (foundPoint) {
      this.tripPlannerFacade.removePoint(foundPoint);
    }
  }

  private async openLoginModal(text: string) {
    let redirect = '';
    if (this.point?.id) {
      redirect = `/listing/${this.point.id}`;
    } else if (this.boondockPoint?.id) {
      redirect = `/boondocking/${this.boondockPoint.id}`;
    } else if (this.poi?.id) {
      redirect = `${location?.pathname}`;
    }

    const modal = await this.modalController.create({
      component: LoginFirstModalComponent,
      componentProps: {
        text,
        redirect,
      },
      cssClass: 'confirm-modal',
    });
    await modal.present();
  }
}
